import _ from 'lodash'
import moment from 'moment-timezone'
import React from 'react'

import { dateShortcuts, dateShortcutsByKey } from 'shared-libs/models/datetime/shortcuts'
import {
  relativeDateModes,
  FIXED,
  RELATIVE,
  RelativeDateFilter,
  USER_TIMEZONE_OPTION,
} from 'shared-libs/models/datetime/relative-date'

import { SelectField } from 'browser/components/atomic-elements/molecules/fields/select-field'
import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Input } from 'browser/components/atomic-elements/atoms/input/input'
import { DateField } from 'browser/components/atomic-elements/molecules/fields/date-field'
import { Footer } from 'browser/components/atomic-elements/atoms/footer/footer'
import { ErrorBlock } from 'browser/components/atomic-elements/atoms/error-block/error-block'
import { SectionHeader } from 'browser/components/atomic-elements/atoms/section/section-header/section-header'

import 'browser/components/atomic-elements/atoms/date-picker/_relative-date-picker.scss'
import { Checkbox } from '../checkbox/checkbox'

type DatePath = 'startDate' | 'endDate'
type DateValue = string | moment.Moment

// fixed dates are stored as moments by this picker and as strings by the date field
type RelativeDateValue = Omit<RelativeDateFilter, 'date' | 'offsetValue'> & {
  date?: DateValue
  offsetValue?: string | number
}

type EditableField = 'date' | 'offsetValue' | 'offsetUnit' | 'timezone'

// either date can be unchecked; older filters only have gte/lte
interface RelativeDateRangeValue {
  startDate?: RelativeDateValue
  endDate?: RelativeDateValue
  gte?: DateValue
  lte?: DateValue
}

export interface IRelativeDateRangePickerProps extends IBaseProps {
  onChange: (value: object, isShortcutClicked?: boolean) => void
  onClose?: () => void
  filter: RelativeDateRangeValue
  onResize?: () => void
}

interface IRelativeDateRangePickerState {
  filter: RelativeDateRangeValue
}

function getRelativeDateMode(offsetUnit: string | undefined) {
  const mode = relativeDateModes.find(
    (relativeDateMode) => relativeDateMode.getKey() === offsetUnit
  )
  if (!mode) {
    throw new TypeError(`Unknown relative date unit: ${offsetUnit}`)
  }
  return mode
}

function getRelativeDate(filter: RelativeDateRangeValue, datePath: DatePath) {
  const relativeDate = filter[datePath]
  if (!relativeDate) {
    throw new TypeError(`Missing ${datePath}`)
  }
  return relativeDate
}

export class RelativeDateRangePicker extends React.Component<
  IRelativeDateRangePickerProps,
  IRelativeDateRangePickerState
> {
  // save latest user values for convenience when toggling a date back on
  private savedUserValues: Partial<Record<DatePath, RelativeDateValue>> = {}

  constructor(props: IRelativeDateRangePickerProps) {
    super(props)
    const newFilter = _.cloneDeep(props.filter)
    const tz = moment.tz.guess()

    if (!newFilter.startDate && !newFilter.endDate) {
      // only initialize if both are missing, as one being missing could be intential
      newFilter.startDate = { mode: FIXED, date: newFilter.gte || moment.utc().startOf('day') }
      newFilter.endDate = { mode: FIXED, date: newFilter.lte || moment.utc().startOf('day') }
    }

    delete newFilter.gte
    delete newFilter.lte

    if (newFilter.startDate && !newFilter.startDate.timezone) {
      newFilter.startDate.timezone = tz
    }

    if (newFilter.endDate && !newFilter.endDate.timezone) {
      newFilter.endDate.timezone = tz
    }

    this.state = {
      filter: newFilter,
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IRelativeDateRangePickerProps) {
    if (this.state && !_.isEqual(this.props.filter, nextProps.filter)) {
      const { startDate, endDate } = this.state.filter

      this.setState({
        filter: _.merge(nextProps.filter, { startDate, endDate }),
      })
    }
  }

  public render() {
    const { filter } = this.state
    const errors = this.getErrorsFromFilter(filter)
    const warnings = this.getWarningsFromFilter(filter)

    return (
      <div className="c-relativeDateRangePicker">
        <div className="c-relativeDateRangePicker-dates">
          {this.renderDateInputs('startDate', 'Start Date')}
          {this.renderDateInputs('endDate', 'End Date')}
        </div>

        <SelectField
          className="mv3 mh3"
          onChange={this.applyShortcut}
          options={dateShortcuts}
          optionLabelPath="label"
          optionValuePath="key"
          placeholder="Select Pre-Configured Time Period"
        />
        {this.renderErrors(warnings, true)}
        {this.renderErrors(errors)}

        <Footer
          onCancelButtonClick={this.props.onClose}
          onPrimaryButtonClick={this.handleFilterApply}
          isPrimaryButtonDisabled={errors.length !== 0}
          primaryButtonText="Apply"
        />
      </div>
    )
  }

  public getWarningsFromFilter(filter: RelativeDateRangeValue) {
    const errors: string[] = []

    if (filter.startDate?.mode === RELATIVE && filter.endDate?.mode === FIXED) {
      errors.push(
        'Mixing a relative start date with a fixed end date may eventually cause the two dates to cross over'
      )
    }

    return errors
  }

  public handleFilterApply = () => {
    const { filter } = this.state
    const { onChange, onClose } = this.props

    onChange(filter)
    if (!onClose) {
      throw new TypeError('onClose is not a function')
    }
    onClose()
  }

  public renderDateInputs(datePath: DatePath, label: string) {
    const { filter } = this.state
    const relativeDate = filter[datePath]

    const enabled = !!relativeDate

    return (
      <div className="c-relativeDatePicker">
        <div className="mh3">
          <SectionHeader
            title={label}
            className="mv2"
            headerControls={<Checkbox value={enabled} onChange={this.handleChecked(datePath)} />}
          />
          {relativeDate && this.renderDateInputContents(datePath, relativeDate)}
        </div>
      </div>
    )
  }

  public renderDateInputContents(datePath: DatePath, relativeDate: RelativeDateValue) {
    return (
      <>
        <SelectField
          onChange={this.handleDateModeChange(datePath)}
          options={this.getRelativeDateModeOptions()}
          optionLabelPath="label"
          optionValuePath="value"
          value={relativeDate.mode}
        />
        <DateField
          value={this.getFixedDate(relativeDate)}
          isDisabled={relativeDate.mode === RELATIVE}
          onChange={this.setFieldUnderDatePath(datePath, 'date')}
          defaultHours={0}
          defaultMinutes={0}
          defaultTimezone={'UTC'}
        />
        {this.renderRelativeDateInputs(datePath, relativeDate)}
      </>
    )
  }

  public getErrorsFromDate(relativeDate: RelativeDateValue | undefined, label: string) {
    const errors: string[] = []

    if (!relativeDate) {
      return errors
    }

    if (relativeDate.mode === RELATIVE) {
      if (relativeDate.offsetUnit !== 'now') {
        if (Number.isNaN(Number(relativeDate.offsetValue))) {
          errors.push(`Please provide an relative offset for ${label}`)
        }
      }
    } else {
      if (!moment.utc(relativeDate.date).isValid()) {
        errors.push(`Please specify a date for ${label}`)
      }
    }

    return errors
  }

  public getErrorsFromFilter(filter: RelativeDateRangeValue) {
    const errors: string[] = []

    if (!filter.startDate && !filter.endDate) {
      errors.push('Please provide at least the start or end date')
    }

    errors.push(...this.getErrorsFromDate(filter.startDate, 'start date'))
    errors.push(...this.getErrorsFromDate(filter.endDate, 'end date'))

    return errors
  }

  public renderErrors(errors: string[], isWarning = false) {
    if (errors.length === 0) {
      return
    }

    return (
      <ErrorBlock
        errorText={
          <div className="ph4 pv2">
            <ul>
              {errors.map((e, index) => (
                <li key={index}>{e}</li>
              ))}
            </ul>
          </div>
        }
        isWarning={isWarning}
      />
    )
  }

  public renderRelativeDateInputs(datePath: DatePath, relativeDate: RelativeDateValue) {
    if (relativeDate.mode !== RELATIVE) {
      return
    }

    let maybeValueInput = null
    if (relativeDate.offsetUnit !== 'now') {
      maybeValueInput = (
        <Input
          inputType="number"
          placeholder="0"
          onChange={this.setFieldUnderDatePath(datePath, 'offsetValue')}
          value={relativeDate.offsetValue}
        />
      )
    }

    const relativeUnitOptions = relativeDateModes.map((option) => {
      return {
        value: option.getKey(),
        label: option.label,
      }
    })

    return (
      <div>
        <SelectField
          onChange={this.setFieldUnderDatePath(datePath, 'offsetUnit')}
          options={relativeUnitOptions}
          optionLabelPath="label"
          optionValuePath="value"
          value={relativeDate.offsetUnit}
        />
        {maybeValueInput}
        {this.renderTimezoneSelect(datePath, relativeDate)}
      </div>
    )
  }

  public renderTimezoneSelect(datePath: DatePath, relativeDate: RelativeDateValue) {
    const timezones = _.flatten([
      moment.tz.zonesForCountry('US'),
      moment.tz.zonesForCountry('CA'),
      moment.tz.zonesForCountry('MX'),
    ])
    const timezonesAsOptions = timezones.map((tz) => ({
      label: `${moment.tz(tz).format('z')} - ${tz}`,
      value: tz,
    }))
    timezonesAsOptions.unshift(USER_TIMEZONE_OPTION)

    return (
      <SelectField
        options={timezonesAsOptions}
        onChange={this.setFieldUnderDatePath(datePath, 'timezone')}
        value={relativeDate.timezone}
      />
    )
  }

  public setFieldUnderDatePath = <K extends EditableField>(datePath: DatePath, fieldPath: K) => {
    const { onResize } = this.props

    return (value: RelativeDateValue[K]) => {
      const { filter } = this.state
      getRelativeDate(filter, datePath)[fieldPath] = value
      this.setState({ filter }, onResize)
    }
  }

  public getFixedDate(relativeDate: RelativeDateValue) {
    if (relativeDate.mode === RELATIVE) {
      const offsetUnit = relativeDate.offsetUnit
      const offsetValue = relativeDate.offsetValue

      if (offsetUnit && offsetValue) {
        return getRelativeDateMode(offsetUnit).calculateDate(moment.utc(), offsetValue)
      } else {
        return null
      }
    }

    return moment.utc(relativeDate.date)
  }

  public handleChecked(datePath: DatePath) {
    return (checked: boolean) => {
      const { onResize } = this.props
      const { filter } = this.state

      if (checked) {
        // restore
        filter[datePath] = this.savedUserValues[datePath] ?? {
          mode: FIXED,
          date: moment.utc().startOf('day'),
        }
      } else {
        // save
        this.savedUserValues[datePath] = filter[datePath]
        delete filter[datePath]
      }

      this.setState({ filter }, onResize)
    }
  }

  public handleDateModeChange(datePath: DatePath) {
    return (mode: string) => {
      const { filter } = this.state
      const relativeDate = getRelativeDate(filter, datePath)
      const oldMode = relativeDate.mode

      if (oldMode == mode) {
        return
      }

      relativeDate.mode = mode

      if (mode == RELATIVE) {
        const oldDate = relativeDate.date
        let newOffset = moment.utc().diff(moment.utc(oldDate), 'days')

        if (newOffset < 0) {
          newOffset = 0
        }

        relativeDate.offsetUnit = 'days'
        relativeDate.offsetValue = newOffset

        delete relativeDate.date
      } else {
        const oldOffsetUnit = relativeDate.offsetUnit
        const oldOffsetValue = relativeDate.offsetValue

        const relativeDateMode = getRelativeDateMode(oldOffsetUnit)
        relativeDate.date = relativeDateMode.calculateDate(moment.utc(), oldOffsetValue)

        delete relativeDate.offsetUnit
        delete relativeDate.offsetValue
      }

      const { onResize } = this.props
      this.setState({ filter }, onResize)
    }
  }

  public getRelativeDateModeOptions() {
    return [
      {
        value: FIXED,
        label: 'Fixed Date',
      },
      {
        value: RELATIVE,
        label: 'Relative Date Offset',
      },
    ]
  }

  public applyShortcut = (shortcutKey: string) => {
    const shortcut = dateShortcutsByKey[shortcutKey]

    const { startDate, endDate } = shortcut.getOffsets()
    const { filter } = this.state

    // an unchecked date has no timezone to keep, so use the constructor default
    const defaultTimezone = moment.tz.guess()
    startDate.timezone = filter.startDate?.timezone ?? defaultTimezone
    endDate.timezone = filter.endDate?.timezone ?? defaultTimezone

    filter.startDate = startDate
    filter.endDate = endDate

    const { onChange, onClose } = this.props
    onChange(filter)
    if (!onClose) {
      throw new TypeError('onClose is not a function')
    }
    onClose()
  }
}
